import React, { useContext } from 'react';
import { observer } from 'mobx-react';
import { Link as RouterLink } from 'react-router-dom';
import { Link, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { ActivityLogEntry, ActivityLogSortField } from '@graphql';
import { appContext, formatTimestamp } from '@utils';

const PAGE_SIZES = [25, 50, 100];

const columns: GridColDef<ActivityLogEntry>[] = [
  {
    field: 'createdAt',
    headerName: 'Timestamp',
    flex: 1,
    minWidth: 190,
    renderCell: ({ row }) => (
      <Tooltip title={new Date(row.createdAt).toISOString()}>
        <span>{formatTimestamp(row.createdAt)}</span>
      </Tooltip>
    ),
  },
  {
    field: 'administrator',
    headerName: 'Administrator',
    flex: 1,
    minWidth: 160,
    renderCell: ({ row }) => {
      if (!row.administrator) return <Typography variant="body2" color="text.secondary">System</Typography>;
      const { name, email, deleted } = row.administrator;
      return `${name ?? email ?? row.administrator.id}${deleted ? ' (deleted)' : ''}`;
    },
  },
  {
    field: 'organization',
    headerName: 'Organization',
    flex: 1,
    minWidth: 160,
    renderCell: ({ row }) => {
      if (!row.organization) return '—';
      const { id, name, deleted } = row.organization;
      if (deleted) return `${name ?? id} (deleted)`;
      return (
        <Link component={RouterLink} to={`/organizations/${id}`} onClick={(event) => event.stopPropagation()}>
          {name ?? id}
        </Link>
      );
    },
  },
  {
    field: 'action',
    headerName: 'Action',
    flex: 1,
    minWidth: 200,
    renderCell: ({ row }) => (
      <Tooltip title={row.action}>
        <span>{row.actionLabel ?? row.action}</span>
      </Tooltip>
    ),
  },
  {
    field: 'target',
    headerName: 'Target',
    flex: 1,
    minWidth: 160,
    renderCell: ({ row }) => {
      if (!row.target) return '—';
      return row.target.label ?? (row.target.id ? `${row.target.type} · ${row.target.id}` : row.target.type);
    },
  },
];

export const ActivityLogTable = observer(() => {
  const { activityLogStore } = useContext(appContext);
  const { items, total, page, pageSize, isLoading, sortField, sortDirection, setPage, setPageSize, setSort } =
    activityLogStore;

  const sortModel: GridSortModel = [{ field: sortField, sort: sortDirection === 'ASC' ? 'asc' : 'desc' }];

  return (
    <DataGrid
      autoHeight
      rows={items}
      columns={columns}
      rowCount={total}
      loading={isLoading}
      paginationMode="server"
      sortingMode="server"
      page={page}
      pageSize={pageSize}
      rowsPerPageOptions={PAGE_SIZES}
      onPageChange={setPage}
      onPageSizeChange={setPageSize}
      sortModel={sortModel}
      sortingOrder={['desc', 'asc']}
      onSortModelChange={(model) => {
        const [first] = model;
        // Clicking a sorted column a third time clears the model; fall back to newest first.
        if (!first) setSort('createdAt', 'DESC');
        else setSort(first.field as ActivityLogSortField, first.sort === 'asc' ? 'ASC' : 'DESC');
      }}
      disableSelectionOnClick
      disableColumnMenu
      isRowSelectable={() => false}
    />
  );
});
